import {
  Body1,
  Button,
  makeStyles,
  mergeClasses,
  Title3,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { GoldStars, IdentitySurface } from '#src/theme/IdentitySurface';
import { GOLD, NAVY } from '#src/theme/identity';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalXXXL}`,
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '1680px',
    // A página ocupa ao menos a área visível: a barra flutuante fica embaixo
    // mesmo com pouco conteúdo.
    minHeight: '100%',
    marginInline: 'auto',
    '@media (max-width: 639px)': {
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalXXL}`,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    minHeight: '40px',
  },
  // O banner de toda página, na superfície azul da identidade: os tokens aqui
  // dentro já são os do tema azul. Baixo, para não tomar a página.
  banner: {
    position: 'relative',
    overflow: 'hidden',
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorTransparentStroke}`,
    '@media (max-width: 639px)': { padding: tokens.spacingHorizontalL },
  },
  // Só com a identidade: brilho suave, o azul que clareia para a direita e
  // o filete dourado embaixo, como o rodapé da etiqueta. O fundo cobre também
  // a borda (que só aparece no alto contraste): sem isso, o degradê se repetia
  // nela e deixava um filete claro na esquerda.
  bannerIdentity: {
    boxShadow: tokens.shadow8,
    backgroundOrigin: 'border-box',
    backgroundRepeat: 'no-repeat',
    backgroundImage: `radial-gradient(circle at 92% -40%, rgba(255, 255, 255, 0.14) 0, transparent 45%), linear-gradient(115deg, ${NAVY.base} 0%, ${NAVY.raised} 45%, ${NAVY.light} 120%)`,
    '::after': {
      content: '""',
      position: 'absolute',
      left: '-1px',
      right: '-1px',
      bottom: '-1px',
      height: '3px',
      backgroundImage: `linear-gradient(90deg, ${GOLD.base} 0%, ${GOLD.base} 35%, transparent 85%)`,
    },
  },
  // Estrelas e ações à direita, na mesma linha: as estrelas não encostam
  // nos botões e ficam sozinhas quando a página não tem ações.
  trailing: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    marginLeft: 'auto',
  },
  bannerStars: {
    flexShrink: 0,
    color: tokens.colorCompoundBrandForeground1,
    '@media (max-width: 639px)': { display: 'none' },
  },
  // O conteúdo acima da decoração.
  bannerContent: { position: 'relative' },
  heroIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorSubtleBackgroundHover,
    boxShadow: `inset 0 0 0 1px ${tokens.colorNeutralStroke1}`,
    color: tokens.colorCompoundBrandForeground1,
    fontSize: '22px',
  },
  heroDescription: { color: tokens.colorNeutralForeground2, maxWidth: '760px' },
  titleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  back: { flexShrink: 0 },
  titleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  // O h1 vem com a margem do navegador; o espaçamento é o do banner.
  title: { margin: 0 },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
  },
  // Presa ao pé da área que rola, centrada no conteúdo (não na janela, que
  // inclui a barra lateral). No fim da página, para logo abaixo dele.
  floating: {
    position: 'sticky',
    bottom: tokens.spacingVerticalL,
    marginTop: 'auto',
    alignSelf: 'center',
    maxWidth: '100%',
    zIndex: 2,
  },
});

export interface PageShellProps {
  readonly title: string;
  readonly description?: string;
  /** Ícone da página, no banner (o mesmo da navegação). */
  readonly icon?: ReactNode;
  /** Ao lado do título (contador, situação). */
  readonly badge?: ReactNode;
  readonly actions?: ReactNode;
  /** Com ele, aparece o botão de voltar antes do título. */
  readonly onBack?: () => void;
  /** Barra flutuante no pé da página (ex.: `FloatingActionBar`). */
  readonly floating?: ReactNode;
  readonly children?: ReactNode;
}

function BackButton({ onBack }: { onBack: () => void }) {
  const styles = useStyles();
  return (
    <Tooltip content="Voltar" relationship="label">
      <Button
        className={styles.back}
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        aria-label="Voltar"
        onClick={onBack}
      />
    </Tooltip>
  );
}

type HeaderProps = Pick<
  PageShellProps,
  'title' | 'description' | 'badge' | 'actions' | 'onBack'
>;

/** Banner da identidade: azul-marinho, ícone, estrelas e filete dourados. */
function BannerHeader({
  icon,
  title,
  description,
  badge,
  actions,
  onBack,
}: HeaderProps & { icon?: ReactNode }) {
  const styles = useStyles();
  return (
    <IdentitySurface
      kind="banner"
      className={styles.banner}
      identityClassName={styles.bannerIdentity}
    >
      <header className={mergeClasses(styles.header, styles.bannerContent)}>
        <div className={styles.titleBlock}>
          {onBack && <BackButton onBack={onBack} />}
          {icon !== undefined && (
            <span className={styles.heroIcon} aria-hidden>
              {icon}
            </span>
          )}
          <div className={styles.titleText}>
            <div className={styles.titleRow}>
              <Title3 as="h1" className={styles.title}>
                {title}
              </Title3>
              {badge}
            </div>
            {description !== undefined && (
              <Body1 className={styles.heroDescription}>{description}</Body1>
            )}
          </div>
        </div>
        <div className={styles.trailing}>
          <GoldStars className={styles.bannerStars} />
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      </header>
    </IdentitySurface>
  );
}

/**
 * Moldura de toda página: o banner da identidade como cabeçalho, o conteúdo
 * e a barra flutuante.
 */
export function PageShell({
  icon,
  floating,
  children,
  ...header
}: PageShellProps) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      <BannerHeader icon={icon} {...header} />
      <div className={styles.content}>{children}</div>
      {floating && <div className={styles.floating}>{floating}</div>}
    </div>
  );
}
